import type { FormAnswers } from "@bjalon/form-runtime";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  type DocumentData,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
  type SnapshotOptions,
} from "firebase/firestore";
import { firestore } from "../lib/firebase";
import type {
  QuestionnaireAnswerRecord,
  QuestionnaireAnswerStatus,
  SessionPublicQuestionnaire,
} from "../types/access";
import type { QuestionnaireRuntimeStatus } from "../types/session";
import { asRecord, toDate } from "../utils/firestoreData";
import { writeAuditEvent } from "./auditService";

const SESSIONS_COLLECTION = "sessions";
const QUESTIONNAIRE_ANSWERS_COLLECTION = "questionnaireAnswers";
const SESSION_PUBLIC_COLLECTION = "sessionPublic";
const SESSION_RUNTIME_COLLECTION = "sessionRuntime";
const PUBLIC_QUESTIONNAIRES_COLLECTION = "questionnaires";

type QuestionnaireRuntimeTransition =
  | "open"
  | "close_to_new_entries"
  | "close"
  | "publish_correction";

function questionnaireAnswerId({
  questionnaireRuntimeId,
  uid,
}: {
  questionnaireRuntimeId: string;
  uid: string;
}): string {
  return `${uid}_${questionnaireRuntimeId}`;
}

function normalizeAnswerStatus(value: unknown): QuestionnaireAnswerStatus {
  return value === "submitted" ? "submitted" : "draft";
}

function normalizeQuestionnaireAnswer(
  id: string,
  data: Record<string, unknown>,
): QuestionnaireAnswerRecord {
  return {
    answersJson: asRecord(data.answersJson),
    id: typeof data.id === "string" ? data.id : id,
    participantSlotId:
      typeof data.participantSlotId === "string" ? data.participantSlotId : "",
    questionnaireRuntimeId:
      typeof data.questionnaireRuntimeId === "string"
        ? data.questionnaireRuntimeId
        : "",
    sourceQuestionnaireId:
      typeof data.sourceQuestionnaireId === "string"
        ? data.sourceQuestionnaireId
        : "",
    startedAt: toDate(data.startedAt),
    status: normalizeAnswerStatus(data.status),
    submittedAt: toDate(data.submittedAt),
    uid: typeof data.uid === "string" ? data.uid : "",
    updatedAt: toDate(data.updatedAt),
    versionId: typeof data.versionId === "string" ? data.versionId : "",
  };
}

const questionnaireAnswerConverter: FirestoreDataConverter<QuestionnaireAnswerRecord> =
  {
    fromFirestore(
      snapshot: QueryDocumentSnapshot<DocumentData>,
      options: SnapshotOptions,
    ): QuestionnaireAnswerRecord {
      return normalizeQuestionnaireAnswer(
        snapshot.id,
        asRecord(snapshot.data(options)),
      );
    },
    toFirestore(value: QuestionnaireAnswerRecord): DocumentData {
      return value;
    },
  };

function sessionPublicQuestionnaireRef({
  questionnaireRuntimeId,
  sessionId,
}: {
  questionnaireRuntimeId: string;
  sessionId: string;
}) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(
    firestore,
    SESSION_PUBLIC_COLLECTION,
    sessionId,
    PUBLIC_QUESTIONNAIRES_COLLECTION,
    questionnaireRuntimeId,
  );
}

function sessionRuntimeQuestionnaireRef({
  questionnaireRuntimeId,
  sessionId,
}: {
  questionnaireRuntimeId: string;
  sessionId: string;
}) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(
    firestore,
    SESSION_RUNTIME_COLLECTION,
    sessionId,
    PUBLIC_QUESTIONNAIRES_COLLECTION,
    questionnaireRuntimeId,
  );
}

function questionnaireAnswerRef({
  answerId,
  sessionId,
}: {
  answerId: string;
  sessionId: string;
}) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(
    firestore,
    SESSIONS_COLLECTION,
    sessionId,
    QUESTIONNAIRE_ANSWERS_COLLECTION,
    answerId,
  ).withConverter(questionnaireAnswerConverter);
}

function assertRuntimeTransition({
  currentStatus,
  transition,
}: {
  currentStatus: QuestionnaireRuntimeStatus;
  transition: QuestionnaireRuntimeTransition;
}): QuestionnaireRuntimeStatus {
  if (transition === "open" && currentStatus === "not_opened") {
    return "open";
  }

  if (transition === "close_to_new_entries" && currentStatus === "open") {
    return "closed_to_new_entries";
  }

  if (
    transition === "close" &&
    (currentStatus === "open" || currentStatus === "closed_to_new_entries")
  ) {
    return "closed";
  }

  if (transition === "publish_correction" && currentStatus === "closed") {
    return "correction_visible";
  }

  throw new Error("Transition questionnaire non autorisée.");
}

function formAnswersToDocument(answers: FormAnswers): Record<string, unknown> {
  return Object.fromEntries(Object.entries(answers));
}

export async function updateQuestionnaireRuntimeStatus({
  actorUid,
  questionnaire,
  sessionId,
  transition,
}: {
  actorUid: string;
  questionnaire: SessionPublicQuestionnaire;
  sessionId: string;
  transition: QuestionnaireRuntimeTransition;
}): Promise<QuestionnaireRuntimeStatus> {
  const nextStatus = assertRuntimeTransition({
    currentStatus: questionnaire.runtimeStatus,
    transition,
  });
  const timestampField =
    nextStatus === "open"
      ? "openedAt"
      : nextStatus === "closed_to_new_entries"
        ? "closedToNewEntriesAt"
        : nextStatus === "closed"
          ? "closedAt"
          : "correctionVisibleAt";
  const nextCorrectionVisible = nextStatus === "correction_visible";
  const nextData = {
    correctionVisible: nextCorrectionVisible,
    runtimeStatus: nextStatus,
    [timestampField]: serverTimestamp(),
  };

  await Promise.all([
    updateDoc(
      sessionPublicQuestionnaireRef({
        questionnaireRuntimeId: questionnaire.id,
        sessionId,
      }),
      nextData,
    ),
    setDoc(
      sessionRuntimeQuestionnaireRef({
        questionnaireRuntimeId: questionnaire.id,
        sessionId,
      }),
      {
        correctionVisible: nextCorrectionVisible,
        id: questionnaire.id,
        sessionId,
        sourceQuestionnaireId: questionnaire.sourceQuestionnaireId,
        status: nextStatus,
        updatedAt: serverTimestamp(),
        versionId: questionnaire.versionId,
        [timestampField]: serverTimestamp(),
      },
      { merge: true },
    ),
  ]);

  await writeAuditEvent({
    actorUid,
    eventType: "session.questionnaire.status_updated",
    metadata: {
      nextStatus,
      questionnaireRuntimeId: questionnaire.id,
      sessionId,
      transition,
    },
  });

  return nextStatus;
}

export async function listQuestionnaireAnswersForSession(
  sessionId: string,
): Promise<QuestionnaireAnswerRecord[]> {
  if (!firestore) {
    return [];
  }

  const answersQuery = query(
    collection(
      firestore,
      SESSIONS_COLLECTION,
      sessionId,
      QUESTIONNAIRE_ANSWERS_COLLECTION,
    ).withConverter(questionnaireAnswerConverter),
    orderBy("updatedAt", "desc"),
  );
  const snapshot = await getDocs(answersQuery);
  return snapshot.docs.map((answerDoc) => answerDoc.data());
}

export async function readParticipantQuestionnaireAnswer({
  questionnaireRuntimeId,
  sessionId,
  uid,
}: {
  questionnaireRuntimeId: string;
  sessionId: string;
  uid: string;
}): Promise<QuestionnaireAnswerRecord | null> {
  const answerId = questionnaireAnswerId({
    questionnaireRuntimeId,
    uid,
  });
  const snapshot = await getDoc(questionnaireAnswerRef({ answerId, sessionId }));
  return snapshot.exists() ? snapshot.data() : null;
}

export async function saveParticipantQuestionnaireAnswer({
  answers,
  participantSlotId,
  questionnaire,
  sessionId,
  status,
  uid,
}: {
  answers: FormAnswers;
  participantSlotId: string;
  questionnaire: SessionPublicQuestionnaire;
  sessionId: string;
  status: QuestionnaireAnswerStatus;
  uid: string;
}): Promise<QuestionnaireAnswerRecord> {
  const currentAnswer = await readParticipantQuestionnaireAnswer({
    questionnaireRuntimeId: questionnaire.id,
    sessionId,
    uid,
  });

  if (currentAnswer?.status === "submitted") {
    throw new Error("Une réponse soumise n'est plus modifiable.");
  }

  const answerId = questionnaireAnswerId({
    questionnaireRuntimeId: questionnaire.id,
    uid,
  });
  const nextPayload = {
    answersJson: formAnswersToDocument(answers),
    id: answerId,
    participantSlotId,
    questionnaireRuntimeId: questionnaire.id,
    sourceQuestionnaireId: questionnaire.sourceQuestionnaireId,
    startedAt: currentAnswer?.startedAt ?? serverTimestamp(),
    status,
    submittedAt: status === "submitted" ? serverTimestamp() : null,
    uid,
    updatedAt: serverTimestamp(),
    versionId: questionnaire.versionId,
  };

  await setDoc(questionnaireAnswerRef({ answerId, sessionId }), nextPayload);

  const snapshot = await getDoc(questionnaireAnswerRef({ answerId, sessionId }));
  const nextAnswer = snapshot.data();

  if (!nextAnswer) {
    throw new Error("Réponse questionnaire introuvable après sauvegarde.");
  }

  return nextAnswer;
}
